import { addDays, eachDayOfInterval, endOfMonth, endOfWeek, format, isSameDay, isSameMonth, isToday, startOfMonth, startOfWeek } from "date-fns";
import { typeMeta, posterFor } from "../../lib/calendar";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function Chip({ event, onSelect }) {
  const meta = typeMeta(event.event_type);
  return (
    <button
      data-testid={`event-chip-${event.id}`}
      onClick={(e) => { e.stopPropagation(); onSelect(event); }}
      className="w-full text-left px-1.5 py-1 rounded-sm text-[11px] leading-tight truncate transition-transform duration-200 hover:scale-[1.03]"
      style={{ background: `${meta.color}1f`, borderLeft: `2px solid ${meta.color}`, color: "#F5F5F5" }}
      title={event.title}
    >
      <span className="font-mono text-[10px] text-neutral-400 mr-1">{format(new Date(event.start), "HH:mm")}</span>
      {event.sold_out && <span className="text-[9px] font-mono text-loss mr-1">SOLD OUT</span>}
      {event.title}
    </button>
  );
}

export function MonthView({ cursor, events, onSelect }) {
  const gridStart = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd });

  return (
    <div data-testid="month-view">
      <div className="hidden md:grid grid-cols-7 border border-white/10 rounded-md overflow-hidden">
        {DOW.map((d) => (
          <div key={d} className="px-2 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-500 border-b border-white/10 bg-white/[0.02]">
            {d}
          </div>
        ))}
        {days.map((day, i) => {
          const dayEvents = events.filter((e) => isSameDay(new Date(e.start), day));
          return (
            <div
              key={day.toISOString()}
              data-testid={`month-cell-${format(day, "yyyy-MM-dd")}`}
              className={`min-h-[110px] p-1.5 border-b border-r border-white/10 space-y-1 animate-fade-up ${
                isSameMonth(day, cursor) ? "" : "opacity-35"
              } ${(i + 1) % 7 === 0 ? "border-r-0" : ""}`}
              style={{ animationDelay: `${Math.floor(i / 7) * 40}ms` }}
            >
              <div className={`font-mono text-xs mb-1 ${isToday(day) ? "text-black bg-primary px-1.5 rounded-sm inline-block font-bold" : "text-neutral-500"}`}>
                {format(day, "d")}
              </div>
              {dayEvents.map((e) => <Chip key={e.id} event={e} onSelect={onSelect} />)}
            </div>
          );
        })}
      </div>

      <AgendaList
        events={events.filter((e) => isSameMonth(new Date(e.start), cursor))}
        onSelect={onSelect}
        className="md:hidden"
      />
    </div>
  );
}

export function WeekView({ cursor, events, onSelect }) {
  const start = startOfWeek(cursor, { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2" data-testid="week-view">
      {days.map((day, i) => {
        const dayEvents = events.filter((e) => isSameDay(new Date(e.start), day));
        return (
          <div
            key={day.toISOString()}
            className="border border-white/10 rounded-md p-2 min-h-[140px] animate-fade-up bg-white/[0.02]"
            style={{ animationDelay: `${i * 45}ms` }}
            data-testid={`week-cell-${format(day, "yyyy-MM-dd")}`}
          >
            <div className="mb-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">{format(day, "EEE")}</div>
              <div className={`font-display text-lg ${isToday(day) ? "text-primary" : "text-white"}`}>{format(day, "d MMM")}</div>
            </div>
            <div className="space-y-1">
              {dayEvents.length === 0 && <p className="text-[11px] text-neutral-600">No events</p>}
              {dayEvents.map((e) => <Chip key={e.id} event={e} onSelect={onSelect} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function DayView({ cursor, events, onSelect }) {
  const dayEvents = events.filter((e) => isSameDay(new Date(e.start), cursor));
  return (
    <div data-testid="day-view">
      <h3 className="font-display text-xl mb-4">{format(cursor, "EEEE d MMMM yyyy")}</h3>
      {dayEvents.length === 0 ? (
        <p className="text-sm text-neutral-500" data-testid="day-empty">No events scheduled for this day.</p>
      ) : (
        <AgendaList events={dayEvents} onSelect={onSelect} />
      )}
    </div>
  );
}

export function AgendaList({ events, onSelect, className = "" }) {
  if (events.length === 0)
    return <p className={`text-sm text-neutral-500 ${className}`} data-testid="agenda-empty">No events in this period.</p>;
  return (
    <div className={`space-y-2 ${className}`} data-testid="agenda-list">
      {events.map((e, i) => {
        const meta = typeMeta(e.event_type);
        return (
          <button
            key={e.id}
            data-testid={`agenda-item-${e.id}`}
            onClick={() => onSelect(e)}
            className="w-full text-left flex items-center gap-3 p-3 border border-white/10 rounded-md hover:border-primary/60 hover:bg-white/[0.04] transition-colors duration-200 animate-fade-up"
            style={{ animationDelay: `${i * 40}ms` }}
          >
            <div className="w-1 self-stretch rounded-full" style={{ background: meta.color }} />
            <img
              src={posterFor(e)}
              alt=""
              className="h-12 w-12 sm:h-14 sm:w-20 object-cover rounded-sm shrink-0"
              data-testid={`agenda-poster-${e.id}`}
            />
            <div className="min-w-[70px] font-mono text-xs text-neutral-400">
              <div>{format(new Date(e.start), "d MMM")}</div>
              <div className="text-primary">{format(new Date(e.start), "HH:mm")}</div>
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium">{e.title}</div>
              <div className="text-[11px] text-neutral-500">{meta.label} · {e.game}</div>
            </div>
            {e.sold_out ? (
              <span className="text-[10px] font-mono text-loss uppercase">Sold out</span>
            ) : (
              e.ticket_url && <span className="text-[10px] font-mono text-primary uppercase">Tickets</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
